import React from "react";
import { CaseStyle, convertCase, makeClassName, toLocalDateString } from "@hex-insights/core";
import {
	DateTimeField,
	EditMode,
	FieldIf,
	Form,
	FormSubmitFunction,
	NumberField,
	NumberFieldInputProps,
	NumberInput,
	RadioButtonsInput,
	RadioField,
	RadioFieldInputProps,
	RadioOption,
	RadioOptionProps,
	submissionFailure,
	SubmitButton,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
	useFormState,
} from "@hex-insights/forms";
import styles from "./grading-form.module.css";

enum GradingStatus {
	TurnedIn = "Turned In",
	Late = "Late",
	NotTurnedIn = "Not Turned In",
	Excused = "Excused",
}

const gradingStatusOptions = [
	{ value: GradingStatus.TurnedIn },
	{ value: GradingStatus.Late },
	{ value: GradingStatus.NotTurnedIn },
	{ value: GradingStatus.Excused },
];

type GradingFormValues = {
	status: GradingStatus;
	submissionDate: string | null;
	points: number | null;
	notes: string;
};

const initialGradingFormValues: GradingFormValues = {
	status: GradingStatus.TurnedIn,
	submissionDate: null,
	points: null,
	notes: "",
};

export type CourseAssessmentGradingFormProps = {
	totalPoints: number;
};

export function CourseAssessmentGradingForm({ totalPoints }: CourseAssessmentGradingFormProps) {
	const formState = useFormState<GradingFormValues>({
		initialFormValues: () => ({ ...initialGradingFormValues, submissionDate: toLocalDateString(new Date()) }),
		editMode: EditMode.WriteOnly,
	});

	const onSubmit = React.useCallback<FormSubmitFunction<GradingFormValues>>(async () => {
		return submissionFailure([]);
	}, []);

	const includesSubmission =
		formState.formValues.status === GradingStatus.TurnedIn || formState.formValues.status === GradingStatus.Late;

	const WrappedPointsInput = React.useCallback(
		(props: NumberFieldInputProps) => <PointsInput {...props} totalPoints={totalPoints} />,
		[totalPoints],
	);

	return (
		<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)}>
			<Form.Column style={{ width: "100%" }}>
				<RadioField
					formState={formState}
					name="status"
					options={gradingStatusOptions}
					Input={ResponseInput}
					style={{ width: "fit-content" }}
				/>
				<FieldIf formState={formState} name="submissionDate" condition={includesSubmission}>
					<DateTimeField formState={formState} name="submissionDate" label="Turn In Date" />
				</FieldIf>
				<FieldIf formState={formState} name="points" condition={formState.formValues.status !== GradingStatus.Excused}>
					<NumberField formState={formState} name="points" validationUnit={1} min={0} Input={WrappedPointsInput} />
				</FieldIf>
				<TextField
					formState={formState}
					name="notes"
					label="Comments"
					optional
					multiLine
					Input={NotesInput}
					style={{ width: "100%" }}
				/>

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Save
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

function ResponseInput(props: RadioFieldInputProps<GradingStatus>) {
	return (
		<RadioButtonsInput
			{...props}
			Option={ResponseOption}
			optionClassName={styles["status-input__option"]}
			selectedOptionClassName={styles["status-input__option--selected"]}
		/>
	);
}

function ResponseOption({ className, ...props }: RadioOptionProps<GradingStatus>) {
	return (
		<RadioOption
			{...props}
			className={makeClassName(styles[`status-input__option--${convertCase(props.value, CaseStyle.Kebab)}`], className)}
		/>
	);
}

function PointsInput({ totalPoints, className, ...props }: NumberFieldInputProps & { totalPoints: number }) {
	return (
		<label className={makeClassName(styles["points-input__container"], className)}>
			<NumberInput {...props} className={makeClassName(className, styles["points-input"])} />
			<span className={styles["points-input__denominator"]}>/{totalPoints}</span>
		</label>
	);
}

function NotesInput(props: TextFieldInputProps) {
	return <TextAreaInput {...props} minRows={4} />;
}
